import { hasSharedBookingId } from '@/lib/linkedJobs'
import type { CallsignRow, Request } from '@/types/timeline'

import { DAY_MINUTES, clamp, parseTimeToMinutes } from './time'

export interface RequestWithWindow {
  request: Request
  startMinutes: number
  endMinutes: number
  anchorMinutes: number
}

export interface ConflictWindow {
  startMinutes: number
  endMinutes: number
}

export interface RowLayout {
  laneByRequestId: Map<string, number>
  laneCount: number
  conflictRequestIds: Set<string>
  conflictPairCount: number
  breakConflictCount: number
  requests: RequestWithWindow[]
}

export interface LaneCrossover {
  startMinutes: number
  endMinutes: number
  requestIds: string[]
}

export const buildLaneCrossovers = (
  requests: readonly RequestWithWindow[],
  windowStart: number,
  windowEnd: number
): LaneCrossover[] => {
  const visibleRequests = requests
    .map(({ request, startMinutes, endMinutes }) => {
      const start = Math.max(startMinutes, windowStart)
      const end = Math.min(endMinutes, windowEnd)
      return end > start ? { startMinutes: start, endMinutes: end, requestId: request.id } : null
    })
    .filter((request) => request != null)
    .toSorted((a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes)

  const crossovers: LaneCrossover[] = []
  let current: LaneCrossover | null = null

  for (const request of visibleRequests) {
    if (current && request.startMinutes < current.endMinutes) {
      current.endMinutes = Math.max(current.endMinutes, request.endMinutes)
      current.requestIds.push(request.requestId)
    } else {
      if (current && current.requestIds.length > 1) crossovers.push(current)
      current = {
        startMinutes: request.startMinutes,
        endMinutes: request.endMinutes,
        requestIds: [request.requestId],
      }
    }
  }

  if (current && current.requestIds.length > 1) crossovers.push(current)
  return crossovers
}

const findFirstAvailableLane = (laneEndTimes: number[], startMinutes: number): number => {
  const availableLane = laneEndTimes.findIndex((laneEnd) => startMinutes >= laneEnd)
  if (availableLane !== -1) return availableLane

  laneEndTimes.push(startMinutes)
  return laneEndTimes.length - 1
}

export const sortByTime = (a: RequestWithWindow, b: RequestWithWindow): number => {
  if (a.startMinutes !== b.startMinutes) return a.startMinutes - b.startMinutes
  if (a.endMinutes !== b.endMinutes) return a.endMinutes - b.endMinutes
  return a.request.id.localeCompare(b.request.id)
}

export const buildRequestsWithWindow = (row: CallsignRow): RequestWithWindow[] =>
  row.requests
    .map((request) => {
      const startMinutes = parseTimeToMinutes(request.startTime)
      let endMinutes = parseTimeToMinutes(request.endTime)
      if (startMinutes == null || endMinutes == null) return null
      if (endMinutes <= startMinutes) endMinutes += DAY_MINUTES
      if (endMinutes <= startMinutes) return null

      const fallbackAnchor = Math.round((startMinutes + endMinutes) / 2)
      const anchorMinutes =
        parseTimeToMinutes(request.assistanceAnchorTime ?? request.scheduledTime) ??
        clamp(fallbackAnchor, startMinutes, endMinutes)

      return {
        request,
        startMinutes,
        endMinutes,
        anchorMinutes,
      }
    })
    .filter((value): value is RequestWithWindow => value != null)
    .toSorted(sortByTime)

export const buildRowLayout = (
  row: CallsignRow,
  breakWindows: readonly ConflictWindow[] = []
): RowLayout => {
  const requests = buildRequestsWithWindow(row)

  const conflictRequestIds = new Set<string>()
  let conflictPairCount = 0
  let breakConflictCount = 0

  for (let i = 0; i < requests.length; i++) {
    for (let j = i + 1; j < requests.length; j++) {
      const first = requests[i]
      const second = requests[j]
      const overlaps =
        first.startMinutes < second.endMinutes && second.startMinutes < first.endMinutes

      if (overlaps && !hasSharedBookingId(first.request.bookingId, second.request.bookingId)) {
        conflictPairCount += 1
        conflictRequestIds.add(first.request.id)
        conflictRequestIds.add(second.request.id)
      }
    }
  }

  for (const request of requests) {
    for (const breakWindow of breakWindows) {
      const overlaps =
        request.startMinutes < breakWindow.endMinutes &&
        breakWindow.startMinutes < request.endMinutes

      if (overlaps) {
        breakConflictCount += 1
        conflictRequestIds.add(request.request.id)
      }
    }
  }

  const laneByRequestId = new Map<string, number>()
  const laneEndTimes: number[] = []

  for (const item of requests) {
    const lane = findFirstAvailableLane(laneEndTimes, item.startMinutes)
    laneEndTimes[lane] = item.endMinutes
    laneByRequestId.set(item.request.id, lane)
  }

  return {
    laneByRequestId,
    laneCount: Math.max(1, laneEndTimes.length),
    conflictRequestIds,
    conflictPairCount,
    breakConflictCount,
    requests,
  }
}
